import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import { api } from '../api/client.ts';
import { useApiError } from '../api/describe.ts';
import type { CredentialView, DeploymentSummary, LocalizedText, StepView } from '../api/types.ts';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Confirm,
  CopyableValue,
  Notice,
  Skeleton,
} from '../components/ui.tsx';
import {
  DEPLOYMENT_STATUS,
  STEP_STATUS,
  TERMINAL_DEPLOYMENT_STATUSES,
  presentation,
} from '../status.ts';
import './DeploymentScreen.css';

/**
 * Watching an installation, and collecting what it produced.
 *
 * Progress arrives over the event stream; the step list is also loaded up
 * front, so a customer who arrives after it finished sees the same screen as
 * one who watched it happen. The percentage is the proportion of *steps*
 * completed, which is a real number — there is no invented progress bar.
 */

export function DeploymentScreen({ deploymentId }: { deploymentId: string }): ReactNode {
  const { t, pick } = useI18n();
  const describe = useApiError();
  const [deployment, setDeployment] = useState<DeploymentSummary | undefined>();
  const [steps, setSteps] = useState<readonly StepView[]>([]);
  const [messages, setMessages] = useState<Record<string, LocalizedText>>({});
  const [credentials, setCredentials] = useState<readonly CredentialView[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [cancelling, setCancelling] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const finished = useRef(false);
  const [credentialsFailed, setCredentialsFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const detail = await api.deployment(deploymentId);
        if (cancelled) return;
        setDeployment(detail.deployment);
        setSteps(detail.steps);
        if (TERMINAL_DEPLOYMENT_STATUSES.has(detail.deployment.status)) {
          finished.current = true;
          setCredentials(await api.credentials(deploymentId));
        }
      } catch (caught) {
        if (!cancelled) {
          setError(describe(caught));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deploymentId]);

  useEffect(() => {
    if (finished.current) return;

    const close = api.watchDeployment(deploymentId, {
      onState: (state) => {
        setReconnecting(false);
        setDeployment(state.deployment);
        setSteps(state.steps);
      },
      onProgress: (event) => {
        setReconnecting(false);
        if (event.type === 'step.status') {
          setSteps((current) => applyStepStatus(current, event));
        } else if (event.type === 'step.progress') {
          setMessages((current) => ({ ...current, [event.stepKey]: event.message }));
        } else if (event.type === 'deployment.status') {
          setDeployment((current) =>
            current
              ? { ...current, status: event.status, phase: event.phase ?? current.phase }
              : current,
          );
        }
      },
      onFinished: (summary) => {
        finished.current = true;
        setDeployment(summary);
        void api
          .credentials(deploymentId)
          .then(setCredentials)
          // Swallowing this left a customer looking at a successful install
          // with no sign-in details and no explanation — the one moment in
          // the whole product where they most need both. The details are
          // safe on the server; this only says to come back for them.
          .catch(() => setCredentialsFailed(true));
      },
      // The stream reconnects by itself; this only tells the customer why the
      // screen paused, rather than leaving them looking at a frozen list.
      onError: () => setReconnecting(true),
    });

    return close;
  }, [deploymentId]);

  const progress = useMemo(() => {
    const total = steps.length;
    const done = steps.filter(
      (step) => step.status === 'success' || step.status === 'skipped',
    ).length;
    return { done, total, percent: total === 0 ? 0 : Math.round((done / total) * 100) };
  }, [steps]);

  const cancel = async (): Promise<void> => {
    setCancelling(false);
    try {
      await api.cancelDeployment(deploymentId);
      setDeployment((current) => (current ? { ...current, status: 'cancelling' } : current));
    } catch (caught) {
      setError(describe(caught));
    }
  };

  if (error && !deployment) return <Notice tone="danger">{error}</Notice>;
  if (!deployment) {
    return (
      <Card>
        <Skeleton lines={6} />
      </Card>
    );
  }

  const status = presentation(DEPLOYMENT_STATUS, deployment.status, {
    label: 'deployments.statusDraft',
    tone: 'neutral',
  });
  const isDone = TERMINAL_DEPLOYMENT_STATUSES.has(deployment.status);
  const succeeded = deployment.status === 'succeeded';

  return (
    <>
      <header className="page-header">
        <div>
          <h1>
            {isDone
              ? t(succeeded ? 'result.successTitle' : 'result.failedTitle')
              : t('progress.title')}
          </h1>
          <p className="muted">
            {isDone
              ? t(succeeded ? 'result.successBody' : 'result.failedBody')
              : t('progress.subtitle')}
          </p>
        </div>
        <Badge tone={status.tone}>{t(status.label)}</Badge>
      </header>

      {reconnecting ? <Notice tone="neutral">{t('progress.reconnecting')}</Notice> : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {deployment.status === 'cancelling' ? (
        <Notice tone="warning">{t('progress.cancelling')}</Notice>
      ) : null}
      {deployment.failure ? (
        <Notice tone="danger" title={t('result.failedTitle')}>
          {pick(deployment.failure.message)}
        </Notice>
      ) : null}

      {isDone && succeeded && credentials.length > 0 ? (
        <Credentials
          deploymentId={deploymentId}
          credentials={credentials}
          onChange={setCredentials}
        />
      ) : null}

      {/* Succeeded, and we could not fetch the sign-in details. Said out
          loud: the install worked and the details are safe on the server, so
          the answer is to come back rather than to run it again — which is
          exactly what a customer staring at a blank success screen would
          otherwise consider doing. */}
      {isDone && succeeded && credentials.length === 0 && credentialsFailed ? (
        <Notice
          tone="warning"
          title={t('result.credentials')}
          actions={
            <Button variant="secondary" onClick={() => window.location.reload()}>
              {t('common.retry')}
            </Button>
          }
        >
          {t('result.credentialsUnavailable')}
        </Notice>
      ) : null}

      <Card>
        <CardHeader
          title={t('progress.title')}
          description={`${progress.done} / ${progress.total}`}
          actions={
            isDone ? null : (
              <Button size="sm" variant="danger" onClick={() => setCancelling(true)}>
                {t('progress.cancel')}
              </Button>
            )
          }
        />

        {/* Not `window.confirm`: that blocks the page, and the page is a live
            event stream drawing this installation's progress. */}
        {cancelling ? (
          <Confirm
            title={t('progress.cancel')}
            confirmLabel={t('progress.cancel')}
            onCancel={() => setCancelling(false)}
            onConfirm={() => void cancel()}
          >
            {t('progress.cancelConfirm')}
          </Confirm>
        ) : null}

        {/* A percentage of completed steps out of a known total — the only
            kind of percentage this screen is allowed to show. */}
        <div
          className="progress-bar"
          role="progressbar"
          aria-valuenow={progress.percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <span style={{ inlineSize: `${progress.percent}%` }} />
        </div>

        <ol className="steps">
          {steps.map((step) => {
            const stepStatus = presentation(STEP_STATUS, step.status, {
              label: 'progress.stepPending',
              tone: 'neutral',
            });
            const message = messages[step.key];
            return (
              <li key={step.key} className={`steps__item is-${step.status}`}>
                <span className="steps__marker" aria-hidden="true" />
                <div className="steps__body">
                  <span className="steps__title">{pick(step.title)}</span>
                  {message && step.status === 'running' ? (
                    <span className="steps__message">{pick(message)}</span>
                  ) : null}
                  {step.error ? (
                    <span className="steps__error">{pick(step.error.message)}</span>
                  ) : null}
                </div>
                <Badge tone={stepStatus.tone}>{t(stepStatus.label)}</Badge>
              </li>
            );
          })}
        </ol>
      </Card>
    </>
  );
}

function Credentials({
  deploymentId,
  credentials,
  onChange,
}: {
  deploymentId: string;
  credentials: readonly CredentialView[];
  onChange: (credentials: readonly CredentialView[]) => void;
}): ReactNode {
  const { t, pick } = useI18n();
  const describe = useApiError();
  const [busy, setBusy] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  const reveal = async (credential: CredentialView): Promise<void> => {
    setBusy(credential.id);
    setError(undefined);
    try {
      const revealed = await api.revealCredential(deploymentId, credential.id);
      onChange(credentials.map((item) => (item.id === credential.id ? revealed : item)));
    } catch (caught) {
      setError(describe(caught));
    } finally {
      setBusy(undefined);
    }
  };

  const panelUrl = credentials.find((credential) => credential.url)?.url;

  return (
    <Card className="credentials">
      <CardHeader
        title={t('result.credentials')}
        description={t('result.credentialWarning')}
        actions={
          panelUrl ? (
            <Button
              // `noopener` matters: the panel is the customer's own site, but
              // an opened tab with a handle back to this one is a needless
              // hole to leave open.
              onClick={() => window.open(panelUrl, '_blank', 'noopener,noreferrer')}
            >
              {t('result.openPanel')}
            </Button>
          ) : null
        }
      />

      {error ? <Notice tone="danger">{error}</Notice> : null}

      <div className="credentials__list">
        {credentials.map((credential) => (
          <section key={credential.id} className="credential">
            <h3>{pick(credential.label)}</h3>
            {credential.url ? (
              <Row label={t('result.address')}>
                <CopyableValue value={credential.url} />
              </Row>
            ) : null}
            {credential.username ? (
              <Row label={t('result.username')}>
                <CopyableValue value={credential.username} />
              </Row>
            ) : null}
            {credential.hasSecret ? (
              <Row label={t('result.password')}>
                {credential.value ? (
                  <CopyableValue value={credential.value} secret />
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    busy={busy === credential.id}
                    // Not shown by default: every reveal is a recorded event,
                    // and one made by accident is still one that happened.
                    onClick={() => void reveal(credential)}
                  >
                    {t('result.revealOnce')}
                  </Button>
                )}
              </Row>
            ) : null}
          </section>
        ))}
      </div>

      <Notice tone="info" title={t('result.whatNext')}>
        {t('result.whatNextBody')}
      </Notice>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="credential__row">
      <span className="credential__label">{label}</span>
      <div className="credential__value">{children}</div>
    </div>
  );
}

/** Apply one `step.status` event, inserting the step if it is not yet listed. */
function applyStepStatus(
  steps: readonly StepView[],
  event: {
    stepKey: string;
    ordinal: number;
    title: LocalizedText;
    phase: string;
    status: string;
    attempt: number;
    durationMs?: number;
    errorCode?: string;
    errorMessage?: LocalizedText;
  },
): StepView[] {
  const patch = {
    status: event.status,
    attempt: event.attempt,
    durationMs: event.durationMs ?? null,
    error: event.errorMessage
      ? { code: event.errorCode ?? null, message: event.errorMessage }
      : undefined,
  };

  const existing = steps.find((step) => step.key === event.stepKey);
  if (existing) {
    return steps.map((step) => (step.key === event.stepKey ? { ...step, ...patch } : step));
  }
  return [
    ...steps,
    {
      key: event.stepKey,
      ordinal: event.ordinal,
      kind: '',
      title: event.title,
      phase: event.phase,
      startedAt: null,
      finishedAt: null,
      ...patch,
    },
  ].sort((a, b) => a.ordinal - b.ordinal);
}
